import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";

interface Props {
  open: boolean;
  totalComments: number;
  unresolvedComments: number;
  rejectedSectionLabels: string[];
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Spec §5: prompt the officer "Are you sure you have addressed all comments?" before they
 * resubmit a previously rejected report. Warns when comments are still unresolved but lets
 * the officer override (the spec doesn't require hard-blocking).
 */
export function ResubmitConfirmationDialog({
  open,
  totalComments,
  unresolvedComments,
  rejectedSectionLabels,
  onClose,
  onConfirm,
  loading,
}: Props) {
  const theme = useTheme();
  const hasUnresolved = unresolvedComments > 0;
  const heroColor = hasUnresolved
    ? theme.palette.warning.main
    : theme.palette.primary.main;
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="resubmit-dialog-title"
      aria-describedby="resubmit-dialog-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          minWidth: 380,
          maxWidth: 480,
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={loading}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: heroColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: `0 4px 14px ${alpha(heroColor, 0.3)}`,
          }}
        >
          <HelpOutlineIcon sx={{ fontSize: 32, color: "white" }} />
        </Box>

        <Typography
          id="resubmit-dialog-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
        >
          Resubmit for CO review?
        </Typography>

        <Typography
          id="resubmit-dialog-description"
          variant="body1"
          sx={{ color: "text.secondary", lineHeight: 1.6 }}
        >
          Are you sure you have addressed all of the Commanding Officer&apos;s
          comments?
        </Typography>

        {rejectedSectionLabels.length > 0 && (
          <Box
            sx={{
              mt: 3,
              p: 1.75,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, 0.02),
              textAlign: "left",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
            >
              Sections rejected last cycle
            </Typography>
            <List dense disablePadding>
              {rejectedSectionLabels.map((label) => (
                <ListItem disableGutters key={label} sx={{ py: 0.25 }}>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      variant: "body2",
                      fontWeight: 500,
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {hasUnresolved ? (
          <Alert severity="warning" sx={{ mt: 2, textAlign: "left" }}>
            You have <strong>{unresolvedComments}</strong> comment
            {unresolvedComments === 1 ? "" : "s"} still marked unresolved (out of{" "}
            {totalComments} total). The CO will see them as still open. You can
            resubmit anyway if you disagree.
          </Alert>
        ) : (
          totalComments > 0 && (
            <Alert severity="success" sx={{ mt: 2, textAlign: "left" }}>
              All {totalComments} comment{totalComments === 1 ? "" : "s"} marked
              resolved.
            </Alert>
          )
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          gap: 1.5,
          flexDirection: "column",
          alignItems: "stretch",
          "& > :not(:first-of-type)": { marginLeft: 0 },
        }}
      >
        <Button
          onClick={onConfirm}
          color={hasUnresolved ? "warning" : "primary"}
          variant="contained"
          loading={loading}
          fullWidth
          disableElevation
          autoFocus
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: hasUnresolved ? "warning.dark" : "primary.dark",
            },
          }}
        >
          Resubmit
        </Button>
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
          variant="text"
          fullWidth
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
